import type { KeyboardEvent } from 'react';

import type {
  AttrInputConfigType,
  MarketplacePaginatedRes,
} from '../marketplace';

import { BASE_FONT_SCALE, MAX_FONT_SCALE } from '../constants';
import { BaseLanguageType, DEFAULT_CDN_BASE_URL } from '../marketplace';
import { LOCAL_STORAGE_KEY_LANG } from './i18n';

/**
 * @see https://github.com/axios/axios/blob/main/lib/helpers/combineURLs.js
 */
export const combineURLs = (baseURL: string = '', relativeURL: string = '') => {
  return relativeURL
    ? `${baseURL.replace(/\/+$/, '')}/${relativeURL.replace(/^\/+/, '')}`
    : baseURL;
};

/**
 * For JavaScript Date, the timestamp is in milliseconds, it is a helper to normalize the timestamp to milliseconds
 * so that it can be used in Date object
 */
export const toJSTimestamp = (timestampOrDate?: null | number | string) => {
  if (!timestampOrDate) return 1;
  if (
    typeof timestampOrDate === 'number' ||
    Number.isInteger(Number(timestampOrDate))
  ) {
    // If 0 Countdown will not show renderer element
    return (
      Number(timestampOrDate) *
        (timestampOrDate?.toString().length === 13 ? 1 : 1000) || 1
    );
  }

  return new Date(timestampOrDate).getTime();
};

export const toLocaleTimezoneStr = (
  timestampOrDate?: null | number | string
) => {
  if (!timestampOrDate) return '';
  if (
    typeof timestampOrDate === 'number' ||
    Number.isInteger(Number(timestampOrDate))
  ) {
    return new Date(toJSTimestamp(timestampOrDate)).toLocaleString();
  }

  if (timestampOrDate.endsWith('Z')) {
    return new Date(timestampOrDate).toLocaleString();
  }

  // Prevent backend return wrong date format (e.g. 2024-11-11 10:30)
  return new Date(`${timestampOrDate}Z`).toLocaleString();
};

export const isValidTimestamp = (timestamp: unknown) => {
  try {
    const parsedTimestamp = Number(timestamp);
    if (!Number.isInteger(parsedTimestamp)) {
      return false;
    }
    return parsedTimestamp > 0 && parsedTimestamp < 1e13;
  } catch (error) {
    return false;
  }
};

export const groupBy = <T>(arr: T[], fn: (item: T) => any) => {
  return arr.reduce<Record<string, T[]>>((prev, curr) => {
    const groupKey = fn(curr);
    const group = prev[groupKey] || [];
    group.push(curr);
    return { ...prev, [groupKey]: group };
  }, {});
};

export const handleInputMaxLength = (e: KeyboardEvent<HTMLInputElement>) => {
  if ('0123456789'.indexOf(e.key) === -1) return true;
  const target = e.currentTarget as HTMLInputElement;
  if (target.value?.length >= target.maxLength) {
    e.preventDefault();
    return false;
  }
  return true;
};

const NUMBER_ONLY_WHITELIST_REGEX =
  /(^\d*$)|(ArrowDown|ArrowLeft|ArrowRight|ArrowUp|Backspace|Delete|End|Home|Tab|\.)/;
export const handleInputNumberOnly = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  if (
    e.key.match(NUMBER_ONLY_WHITELIST_REGEX) ||
    (['a', 'c', 'v'].includes(e.key.toLowerCase()) && (e.ctrlKey || e.metaKey))
  )
    return true;

  e.preventDefault();
  return false;
};

export const showAddress = (addr?: string, length = 4) => {
  if (!addr) return '';
  return `${addr.substring(0, 2 + length)}...${addr.slice(addr.length - length)}`;
};

export const deepSort = <T = Record<string, any>>(object: T): T => {
  if (Array.isArray(object)) {
    return object.map(deepSort) as T;
  }
  if (typeof object === 'object' && object !== null) {
    return Object.keys(object)
      .sort()
      .reduce((acc, key) => {
        acc[key] = deepSort(object[key as keyof object]);
        return acc;
      }, {} as any);
  }
  return object;
};

/**
 * @see https://github.com/uuidjs/uuid/blob/main/src/regex.js
 */
const UUID_REGEX =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000)$/i;
export const isUUID = (uuid: string) => {
  return UUID_REGEX.test(uuid);
};

export const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

/**
 * Generated by ChatGPT-4o and reviewed by Claude 3.5 Sonnet
 */
export const isValidHttpUrl = (str: string) => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // Protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|' + // Domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // Port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // Query string
      '(\\#[-a-z\\d_]*)?$',
    'i' // Fragment locator
  );
  return !!pattern.test(str);
};

export const isUploadedToCDN = (url?: string) => {
  if (!url) return false;
  return url.startsWith(`${DEFAULT_CDN_BASE_URL}/`);
};

/**
 * For flexible minting input type, there are some type that HTML input type
 * do not have, so make a mapping to convert to HTML input type
 */
export const getHTMLInputType = (type: AttrInputConfigType) => {
  const fileType: AttrInputConfigType[] = [
    'audio',
    'image',
    'video',
    'model',
    'file',
  ];
  if (fileType.includes(type)) return 'file';
  if (type === 'date_time') return 'datetime-local';
  return type;
};

export const safeUpdateFontScale = (scale: number) => {
  try {
    const fontScale =
      scale < BASE_FONT_SCALE || scale > MAX_FONT_SCALE
        ? BASE_FONT_SCALE
        : scale;

    // For tablet + desktop, the base font size should change back to 16px
    const mql = window.matchMedia('(min-width: 1024px)');

    document.documentElement.style.fontSize = `${mql.matches ? fontScale + 25 : fontScale}%`;

    return fontScale;
  } catch (error) {
    return BASE_FONT_SCALE;
  }
};

/**
 * A simple helper to make sure the value is defined, prevent to use if statement / non-null assertion
 */
export function assertDefined<T>(
  value: null | T | undefined
): asserts value is T {
  if (value == null) {
    throw new Error(`Fatal error: value ${value} must be defined.`);
  }
}

/**
 * Check whether the current time is almost end of the a given time range
 */
export const isAlmostEnd = (
  _startAtTimestamp: number,
  _endAtTimestamp: number,
  percentage: number = 0.8
) => {
  const startAtTimestamp = toJSTimestamp(_startAtTimestamp);
  const endAtTimestamp = toJSTimestamp(_endAtTimestamp);
  const now = Date.now();
  const totalDuration = endAtTimestamp - startAtTimestamp;
  const elapsedDuration = now - startAtTimestamp;

  if (elapsedDuration >= totalDuration) {
    return false;
  }

  const percentageElapsed = elapsedDuration / totalDuration;

  return percentageElapsed >= percentage;
};

/**
 * Check whether the current time is upcoming within a given days
 */
export const isUpcoming = (startAtTimestamp: number, days: number = 3) => {
  const now = Date.now();
  const daysInMs = days * 24 * 60 * 60 * 1000;
  const elapsedDuration = toJSTimestamp(startAtTimestamp) - now;

  return elapsedDuration <= daysInMs && elapsedDuration > 0;
};

/**
 * Check if the amount is almost reach the total amount
 */
export const isSmallAmountRemain = (
  totalAmount: number,
  mintedAmount: number,
  percentage: number = 0.8
) => {
  if (totalAmount === 0) return false;
  if (mintedAmount >= totalAmount) return false;
  return mintedAmount / totalAmount >= percentage;
};

/**
 * Calculate the days left from now to the given timestamp
 */
export const dayLeftFromNow = (endAtTimestamp: number) => {
  const nowAt = new Date().getTime();
  const endAt = toJSTimestamp(endAtTimestamp);
  const oneDayInMs = 24 * 60 * 60 * 1000;
  return Math.ceil((endAt - nowAt) / oneDayInMs);
};

export const sleep = (ms: number) => {
  return new Promise<void>((r) => {
    setTimeout(() => r(), ms);
  });
};

/**
 * To check if the data is empty for both infinite query and normal array
 */
export const isEmpty = (
  data?:
    | {
        pageParams: unknown[];
        pages: MarketplacePaginatedRes<unknown[]>[];
      }
    | null
    | unknown[]
) => {
  if (data == null) return true;
  if (Array.isArray(data)) {
    return data.length === 0;
  }

  return data.pages.length === 0 || data.pages[0].data?.length === 0;
};

export const getLanguage = () => {
  return localStorage.getItem(LOCAL_STORAGE_KEY_LANG) || BaseLanguageType.EN;
};

export const generateCryptoSafeString = (bytes = 16) => {
  const array = new Uint8Array(bytes);
  window.crypto.getRandomValues(array);

  let base64String = '';
  for (let i = 0; i < array.length; i += 1) {
    base64String += String.fromCharCode(array[i]);
  }

  const base64 = window.btoa(base64String);

  // Make the string URL-safe
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
